/**
 * The analysis grids in the browser: decoding the API's uint16 layers, sampling (cursor readout, cross-sections,
 * panels), contour lines, instantaneous shade from the DSM heightfield and horizons. Pure and allocation-light (typed
 * arrays), unit tested with node --test.
 *
 * A grid is axis-aligned in local metres: `x0` its west edge, `y1` its north edge, rows north to south; cell (r, c)
 * has its centre at (x0 + (c + ½)·cell, y1 − (r + ½)·cell). Values are row-major, NaN where there is no data.
 */
import type { Vec2 } from "../geo.ts";

export interface GridSpec {
  x0: number;
  y1: number;
  cell_m: number;
  cols: number;
  rows: number;
}

export interface EncodedRaster {
  encoding: "uint16-base64";
  offset: number;
  scale: number;
  min: number;
  max: number;
  units: string;
  data: string;
}

export interface Raster {
  grid: GridSpec;
  values: Float32Array;
  min: number;
  max: number;
}

const NODATA = 65535;

function base64Bytes(b64: string): Uint8Array {
  if (typeof atob === "function") {
    const bin = atob(b64);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }
  // Node (tests) without atob
  const B = (globalThis as unknown as { Buffer: { from(s: string, e: string): Uint8Array } }).Buffer;
  return new Uint8Array(B.from(b64, "base64"));
}

/** An API layer as floats (value = offset + q·scale; q = 65535 → NaN). */
export function decodeRaster(layer: EncodedRaster | null | undefined, grid: GridSpec): Raster | null {
  if (!layer) return null;
  const bytes = base64Bytes(layer.data);
  const n = grid.rows * grid.cols;
  if (bytes.length < n * 2) return null;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const values = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    const q = view.getUint16(i * 2, true);
    values[i] = q === NODATA ? NaN : layer.offset + q * layer.scale;
  }
  return { grid, values, min: layer.min, max: layer.max };
}

/** Fractional (row, col) of a local point; cell centres at integer + ½. */
export function cellOf(g: GridSpec, [x, y]: Vec2): [number, number] {
  return [(g.y1 - y) / g.cell_m, (x - g.x0) / g.cell_m];
}

export function cellCentre(g: GridSpec, row: number, col: number): Vec2 {
  return [g.x0 + (col + 0.5) * g.cell_m, g.y1 - (row + 0.5) * g.cell_m];
}

/** The value of the cell under a point (NaN outside). */
export function sampleNearest(r: Raster, p: Vec2): number {
  const [fr, fc] = cellOf(r.grid, p);
  const row = Math.floor(fr);
  const col = Math.floor(fc);
  if (row < 0 || col < 0 || row >= r.grid.rows || col >= r.grid.cols) return NaN;
  return r.values[row * r.grid.cols + col]!;
}

/** Bilinear between cell centres; falls back to the nearest cell next to missing data. */
export function sampleBilinear(r: Raster, p: Vec2): number {
  const { cols, rows } = r.grid;
  const [fr, fc] = cellOf(r.grid, p);
  const y = fr - 0.5;
  const x = fc - 0.5;
  const r0 = Math.floor(y);
  const c0 = Math.floor(x);
  if (r0 < 0 || c0 < 0 || r0 + 1 >= rows || c0 + 1 >= cols) return sampleNearest(r, p);
  const v = r.values;
  const a = v[r0 * cols + c0]!;
  const b = v[r0 * cols + c0 + 1]!;
  const c = v[(r0 + 1) * cols + c0]!;
  const d = v[(r0 + 1) * cols + c0 + 1]!;
  if (!(Number.isFinite(a) && Number.isFinite(b) && Number.isFinite(c) && Number.isFinite(d))) return sampleNearest(r, p);
  const tx = x - c0;
  const ty = y - r0;
  return (a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty;
}

export interface ProfilePoint {
  /** Distance from the start (m). */
  d: number;
  x: number;
  y: number;
  /** Bilinear value of each raster (NaN without data). */
  values: number[];
}

/** Evenly spaced samples from `a` to `b` (about two per cell, at most `maxPoints`). */
export function profile(rasters: Raster[], a: Vec2, b: Vec2, maxPoints = 400): ProfilePoint[] {
  const length = Math.hypot(b[0] - a[0], b[1] - a[1]);
  const cell = Math.min(...rasters.map((r) => r.grid.cell_m), 1);
  const n = Math.max(2, Math.min(maxPoints, Math.ceil(length / (cell / 2)) + 1));
  const out: ProfilePoint[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const p: Vec2 = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    out.push({ d: length * t, x: p[0], y: p[1], values: rasters.map((r) => sampleBilinear(r, p)) });
  }
  return out;
}

/** Contour segments of `level` by marching squares over the cell centres: a flat list x1, y1, x2, y2, … in local
 * metres (ambiguous saddles split by the centre value). */
export function contourSegments(r: Raster, level: number): number[] {
  const { cols, rows, cell_m } = r.grid;
  const v = r.values;
  const out: number[] = [];
  const cx = (c: number) => r.grid.x0 + (c + 0.5) * cell_m;
  const cy = (row: number) => r.grid.y1 - (row + 0.5) * cell_m;
  const lerp = (a: number, b: number) => (level - a) / (b - a);
  for (let row = 0; row < rows - 1; row++) {
    for (let c = 0; c < cols - 1; c++) {
      const tl = v[row * cols + c]!;
      const tr = v[row * cols + c + 1]!;
      const br = v[(row + 1) * cols + c + 1]!;
      const bl = v[(row + 1) * cols + c]!;
      if (!(Number.isFinite(tl) && Number.isFinite(tr) && Number.isFinite(br) && Number.isFinite(bl))) continue;
      const code = (tl >= level ? 8 : 0) | (tr >= level ? 4 : 0) | (br >= level ? 2 : 0) | (bl >= level ? 1 : 0);
      if (code === 0 || code === 15) continue;
      const x0 = cx(c);
      const x1 = cx(c + 1);
      const y0 = cy(row);
      const y1 = cy(row + 1);
      const top = (): Vec2 => [x0 + (x1 - x0) * lerp(tl, tr), y0];
      const right = (): Vec2 => [x1, y0 + (y1 - y0) * lerp(tr, br)];
      const bottom = (): Vec2 => [x0 + (x1 - x0) * lerp(bl, br), y1];
      const left = (): Vec2 => [x0, y0 + (y1 - y0) * lerp(tl, bl)];
      const seg = (p: Vec2, q: Vec2) => out.push(p[0], p[1], q[0], q[1]);
      const centre = (tl + tr + br + bl) / 4 >= level;
      switch (code) {
        case 1: case 14: seg(left(), bottom()); break;
        case 2: case 13: seg(bottom(), right()); break;
        case 3: case 12: seg(left(), right()); break;
        case 4: case 11: seg(top(), right()); break;
        case 6: case 9: seg(top(), bottom()); break;
        case 7: case 8: seg(left(), top()); break;
        case 5: if (centre) { seg(left(), top()); seg(bottom(), right()); } else { seg(left(), bottom()); seg(top(), right()); } break;
        case 10: if (centre) { seg(top(), right()); seg(left(), bottom()); } else { seg(left(), top()); seg(bottom(), right()); } break;
      }
    }
  }
  return out;
}

/** SVG path data of contour segments (the 2D view draws y flipped: north up). */
export function segmentsPath(segments: number[]): string {
  let d = "";
  for (let i = 0; i < segments.length; i += 4)
    d += `M${segments[i]!.toFixed(2)} ${(-segments[i + 1]!).toFixed(2)}L${segments[i + 2]!.toFixed(2)} ${(-segments[i + 3]!).toFixed(2)}`;
  return d;
}

function inRing(x: number, y: number, ring: Vec2[]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i]!;
    const [xj, yj] = ring[j]!;
    if (yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** Mean of the finite cells whose centres are inside `polygon` (else the cell under its centroid); the backend's
 * shade report uses the same rule. */
export function meanInPolygon(r: Raster, polygon: Vec2[]): number {
  if (!polygon.length) return NaN;
  const g = r.grid;
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const [x, y] of polygon) {
    minx = Math.min(minx, x); maxx = Math.max(maxx, x); miny = Math.min(miny, y); maxy = Math.max(maxy, y);
  }
  const r0 = Math.max(0, Math.floor((g.y1 - maxy) / g.cell_m));
  const r1 = Math.min(g.rows, Math.ceil((g.y1 - miny) / g.cell_m));
  const c0 = Math.max(0, Math.floor((minx - g.x0) / g.cell_m));
  const c1 = Math.min(g.cols, Math.ceil((maxx - g.x0) / g.cell_m));
  let sum = 0;
  let n = 0;
  for (let row = r0; row < r1; row++) {
    for (let c = c0; c < c1; c++) {
      const [x, y] = cellCentre(g, row, c);
      if (!inRing(x, y, polygon)) continue;
      const v = r.values[row * g.cols + c]!;
      if (Number.isFinite(v)) {
        sum += v;
        n++;
      }
    }
  }
  if (n) return sum / n;
  const cx = polygon.reduce((s, p) => s + p[0], 0) / polygon.length;
  const cy = polygon.reduce((s, p) => s + p[1], 0) / polygon.length;
  return sampleNearest(r, [cx, cy]);
}

/** Which cells of a surface heightfield are in shadow with the sun at (azimuth, elevation): a cell is shaded when
 * the surface rises above the ray to the sun anywhere along it. 1 = shade, 0 = sun, 255 = no data. */
export function shadeMask(surface: Raster, azimuthDeg: number, elevationDeg: number, out?: Uint8Array, tolerance = 0.15): Uint8Array {
  const { cols, rows, cell_m } = surface.grid;
  const v = surface.values;
  const mask = out && out.length === v.length ? out : new Uint8Array(v.length);
  if (elevationDeg <= 0) {
    for (let i = 0; i < v.length; i++) mask[i] = Number.isFinite(v[i]!) ? 1 : 255;
    return mask;
  }
  const az = (azimuthDeg * Math.PI) / 180;
  // per step of one cell along the sun's direction: column and row increments, and the ray's rise
  const dc = Math.sin(az);
  const dr = -Math.cos(az);
  const rise = Math.tan((elevationDeg * Math.PI) / 180) * cell_m;
  let top = -Infinity;
  for (let i = 0; i < v.length; i++) if (v[i]! > top) top = v[i]!;
  for (let row = 0; row < rows; row++) {
    for (let c = 0; c < cols; c++) {
      const i = row * cols + c;
      const z0 = v[i]!;
      if (!Number.isFinite(z0)) {
        mask[i] = 255;
        continue;
      }
      const steps = Math.min(400, Math.ceil((top - z0) / rise));
      let shaded = 0;
      for (let s = 1; s <= steps; s++) {
        const rr = Math.round(row + dr * s);
        const cc = Math.round(c + dc * s);
        if (rr < 0 || cc < 0 || rr >= rows || cc >= cols) break;
        const z = v[rr * cols + cc]!;
        if (z > z0 + tolerance + rise * s) {
          shaded = 1;
          break;
        }
      }
      mask[i] = shaded;
    }
  }
  return mask;
}

/** Horizon elevation (degrees) every `stepDeg` of azimuth seen from a point `lift` above the surface. */
export function horizonProfile(surface: Raster, p: Vec2, z0: number, stepDeg = 5, maxDistance = 60): number[] {
  const out: number[] = [];
  const cell = surface.grid.cell_m;
  for (let az = 0; az < 360; az += stepDeg) {
    const s = Math.sin((az * Math.PI) / 180);
    const c = Math.cos((az * Math.PI) / 180);
    let best = 0;
    for (let d = Math.max(0.75, cell); d <= maxDistance; d += d < 10 ? cell : 2 * cell) {
      const z = sampleNearest(surface, [p[0] + d * s, p[1] + d * c]);
      if (Number.isNaN(z)) {
        if (d > 5 && !insideGrid(surface.grid, [p[0] + d * s, p[1] + d * c])) break;
        continue;
      }
      const t = (z - z0) / d;
      if (t > best) best = t;
    }
    out.push((Math.atan(best) * 180) / Math.PI);
  }
  return out;
}

function insideGrid(g: GridSpec, p: Vec2): boolean {
  const [r, c] = cellOf(g, p);
  return r >= 0 && c >= 0 && r < g.rows && c < g.cols;
}

/** The grid's extent as an SVG rectangle in the 2D view (y flipped). */
export function svgRect(g: GridSpec): { x: number; y: number; width: number; height: number } {
  return { x: g.x0, y: -g.y1, width: g.cols * g.cell_m, height: g.rows * g.cell_m };
}
